import { useCallback, useState } from 'react'

import { useApolloClient } from '@apollo/client/react'
import type { ErrorLike, FetchPolicy } from '@apollo/client'

import { PostMediaAsset_Asset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { CommentData_Comment } from '~publish/pages/Community/Comments/fragments/commentData'
import type { CommentData } from '~publish/pages/Community/Comments/fragments/commentData'

import { CommentLastReply_LastReply } from '../../Comments/fragments/commentLastReply'

export const CommentThreadQueryGQL = graphql(
  `
    query CommentThread($input: CommentThreadInput!) {
      commentThread(input: $input) {
        thread {
          ... on BaseComment {
            __typename
            id
            text
            contentType
            publishedAt
            status
            parentId
            serviceType
            isOwnReply
            isDeleted
            publicUrl
            channelId
            organizationId
            metadata {
              ... on GoogleBusinessCommentMetadata {
                userRating
              }
            }
            assets {
              id
              mimeType
              source
              thumbnail
              type
            }
            author {
              username
              name
              avatar
            }
            post {
              id
              text
              dueAt
              channelService
              assets {
                ...PostMediaAsset_Asset
              }
              tags {
                name
                color
              }
            }
            reactions {
              type
            }
            lastReplies {
              ...CommentLastReply_LastReply
            }
            ...CommentData_Comment
          }
          ... on CommentFromThirdParty {
            labels
            suggestions {
              text
              source
            }
            sentimentAnalysis {
              type
            }
          }
          ... on CommentFromBuffer {
            publishingStatus {
              status
            }
          }
        }
      }
    }
  `,
  [PostMediaAsset_Asset, CommentLastReply_LastReply, CommentData_Comment],
)

type CommentThreadQueryResult = ResultOf<typeof CommentThreadQueryGQL>

export type UseCommentThreadResult = {
  getCommentThread: (
    commentId: string,
    options?: { fetchPolicy?: FetchPolicy },
  ) => Promise<{
    originalComment: CommentData | null
    context: CommentData[]
    replies: CommentData[]
  }>
  loading: boolean
  error: ErrorLike | null
}

/**
 * The query spreads `CommentData_Comment`, so its thread nodes are the shape the
 * card tree consumes. This fails to compile if the query stops selecting the
 * fragment — the miniature's fields reach the dialog only through that spread.
 */
const toCommentData = (
  node: CommentThreadQueryResult['commentThread']['thread'][number],
): CommentData => node

export const useCommentThread = (): UseCommentThreadResult => {
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<ErrorLike | null>(null)

  // `useApolloClient().query()` (not `useLazyQuery`) so each call can pick its
  // own `fetchPolicy` — lazy-query exec options don't accept one.
  const getCommentThread = useCallback(
    async (
      commentId: string,
      options?: { fetchPolicy?: FetchPolicy },
    ): Promise<{
      originalComment: CommentData | null
      context: CommentData[]
      replies: CommentData[]
    }> => {
      setLoading(true)
      setError(null)

      try {
        const { data } = await client.query({
          query: CommentThreadQueryGQL,
          variables: {
            input: {
              commentId,
            },
          },
          fetchPolicy: options?.fetchPolicy,
        })

        const thread = (data?.commentThread?.thread ?? []).map(toCommentData)
        const originalCommentIndex = thread.findIndex(
          (comment) => comment.id === commentId,
        )

        // The slices below are positioned relative to the requested comment, so
        // they only mean anything once it is found. Returning early keeps a
        // thread that omits it from splitting into overlapping context/replies.
        if (originalCommentIndex === -1) {
          return { originalComment: null, context: [], replies: [] }
        }

        return {
          originalComment: thread[originalCommentIndex],
          context: thread.slice(0, originalCommentIndex),
          replies: thread.slice(originalCommentIndex + 1),
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error'))
        throw err
      } finally {
        setLoading(false)
      }
    },
    [client],
  )

  return {
    getCommentThread,
    loading,
    error,
  }
}
